/**
 * 水印工具类
 * 支持在指定容器内创建水印
 */

export interface WatermarkConfig {
  /** 水印文本 */
  text: string;
  /** 目标容器选择器 */
  containerSelector: string;
  /** 水印宽度间距，默认400px */
  width?: number;
  /** 水印高度间距，默认200px */
  height?: number;
  /** 水印颜色，默认#efefef */
  color?: string;
  /** 旋转角度，默认-30度 */
  rotate?: number;
  /** 字体大小，默认16px */
  fontSize?: number;
  /** 透明度，默认0.5 */
  opacity?: number;
  /** z-index层级，默认1000 */
  zIndex?: number;
}

export class WatermarkManager {
  private static instance: WatermarkManager;
  private watermarkElements: Map<string, HTMLElement> = new Map();

  static getInstance(): WatermarkManager {
    if (!WatermarkManager.instance) {
      WatermarkManager.instance = new WatermarkManager();
    }
    return WatermarkManager.instance;
  }

  /**
   * 创建水印
   * @param config 水印配置
   */
  createWatermark(config: WatermarkConfig): boolean {
    try {
      // 移除已存在的水印
      this.removeWatermark(config.containerSelector);

      // 查找目标容器
      const targetElement = document.querySelector(
        config.containerSelector
      ) as HTMLElement;
      if (!targetElement) {
        console.warn(`水印目标容器未找到: ${config.containerSelector}`);
        return false;
      }

      // 确保目标元素有相对定位
      const computedStyle = window.getComputedStyle(targetElement);
      if (computedStyle.position === "static") {
        targetElement.style.position = "relative";
      }

      // 创建水印SVG
      const watermarkSvg = this.createWatermarkSvg(config);

      // 创建水印容器
      const watermarkContainer = document.createElement("div");
      watermarkContainer.className = "watermark-container";
      watermarkContainer.style.cssText = `
        position: absolute;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        pointer-events: none;
        z-index: ${config.zIndex || 1000};
        background-image: url("data:image/svg+xml;charset=utf-8,${encodeURIComponent(
          watermarkSvg
        )}");
        background-repeat: repeat;
        background-size: ${config.width || 400}px ${config.height || 200}px;
        opacity: ${config.opacity || 0.5};
      `;

      // 添加到目标元素
      targetElement.appendChild(watermarkContainer);

      // 保存水印元素引用
      this.watermarkElements.set(config.containerSelector, watermarkContainer);

      console.log(`水印创建成功: ${config.containerSelector}`);
      return true;
    } catch (error) {
      console.error("创建水印失败:", error);
      return false;
    }
  }

  /**
   * 移除水印
   * @param containerSelector 容器选择器
   */
  removeWatermark(containerSelector: string): void {
    const existingWatermark = this.watermarkElements.get(containerSelector);
    if (existingWatermark && existingWatermark.parentNode) {
      existingWatermark.parentNode.removeChild(existingWatermark);
      this.watermarkElements.delete(containerSelector);
    }
  }

  /**
   * 移除所有水印
   */
  removeAllWatermarks(): void {
    this.watermarkElements.forEach((element, selector) => {
      if (element && element.parentNode) {
        element.parentNode.removeChild(element);
      }
    });
    this.watermarkElements.clear();
  }

  /**
   * 创建水印SVG
   * @param config 水印配置
   */
  private createWatermarkSvg(config: WatermarkConfig): string {
    const width = config.width || 400;
    const height = config.height || 200;
    const color = config.color || "#efefef";
    const rotate = config.rotate || -30;
    const fontSize = config.fontSize || 16;
    const centerX = width / 2;
    const centerY = height / 2;

    // 计算文本起始位置，让文本从左侧开始显示
    const textX = width * 0.1; // 从左侧10%位置开始
    const textY = centerY;

    const svg = `
      <svg xmlns="http://www.w3.org/2000/svg" width="${width}" height="${height}" viewBox="0 0 ${width} ${height}">
        <text x="${textX}" y="${textY}" 
              font-family="Arial, sans-serif" 
              font-size="${fontSize}" 
              fill="${color}" 
              text-anchor="start" 
              dominant-baseline="middle"
              transform="rotate(${rotate} ${centerX} ${centerY})">
          ${config.text}
        </text>
      </svg>
    `;
    return svg;
  }

  /**
   * 检查水印是否存在
   * @param containerSelector 容器选择器
   */
  hasWatermark(containerSelector: string): boolean {
    return this.watermarkElements.has(containerSelector);
  }
}

// 导出单例实例
export const watermarkManager = WatermarkManager.getInstance();

// 便捷方法
export const createWatermark = (config: WatermarkConfig) =>
  watermarkManager.createWatermark(config);
export const removeWatermark = (containerSelector: string) =>
  watermarkManager.removeWatermark(containerSelector);
export const removeAllWatermarks = () => watermarkManager.removeAllWatermarks();
